import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { createSelector, Store } from '@ngrx/store';
import { flatMap as _flatmap, get as _get, has as _has, some as _some } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { debounceTime, filter, first, take, takeUntil, withLatestFrom } from 'rxjs/operators';

// Store
import { BillAccountActions, BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { PendingRegistration } from '@amfam/billing/registration/data-access';
import { PolicySelectors, PolicySummarySelectors } from '@amfam/policy/data-access';
import { PolicySummary } from '@amfam/policy/models';
import { AnalyticsActions, AnalyticsFacade, AnalyticsSelectors } from '@amfam/shared/analytics';
import {
  BillAccount,
  BillAccountRegistration,
  BillingAnalytics,
  BillingPaymentPaths,
  INVALID_IMPERSONATION_STATUS_CODE
} from '@amfam/shared/models';
import { PrettyBillingAcctNum } from '@amfam/shared/ui/pipes';
import { userQuery } from '@amfam/shared/user';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  CopyService,
  UtilService,
  WindowRef
} from '@amfam/shared/utility/shared-services';
import {
  Action,
  DockingBarService,
  ErrorLevels,
  ErrorObj,
  LoadingSpinnerService,
  ValidationService
} from '@amfam/ui-kit';

@Component({
  selector: 'ds-bill-account-register',
  templateUrl: './bill-account-register.component.html',
  styleUrls: ['./bill-account-register.component.scss'],
  standalone: false
})
export class BillAccountRegisterComponent implements OnInit, OnDestroy, OnChanges {
  @Input() pendingRegistration: PendingRegistration;
  @Input() index: number;
  @Input() count: number;
  @Input() acceptTerms: boolean;
  @Input() isMyAccount: boolean;
  @Output() registrationSuccessEvent = new EventEmitter();
  @Output() registrationErrorEvent = new EventEmitter();

  billAccount: BillAccount;
  billAccountRegisterForm: UntypedFormGroup;
  billAccountTermsForm: UntypedFormGroup;
  emailAddress: string;
  registerAccountCopy = '';

  updatingRegistration: Observable<boolean>;

  optionalCount = '';
  registrationError = false;
  errorList: Array<ErrorObj> = [];
  riskDescriptionArr: string[] = [];
  isContactBillingToggleEnabled = this.feature.isEnabled('contact_billing');
  accountName: AbstractControl;
  policyDescription: string[] = [];
  hasAdvancedPolicy = false;
  // Make the route constants available on the template
  billingPaths = BillingPaymentPaths;

  saveAndContinueBtnContent: string;
  saveBtnContent: string;

  adobeAnalyticsLoaded$: Observable<boolean>;

  private billAccounts: BillAccount[] = [];

  private nickName: string;
  private selectedDate = '7';

  //Has the structure of billing copy
  private componentData: unknown;

  private billAccNickNamesList: string[] = [];

  private allPolicies: PolicySummary[] = [];

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private copyService: CopyService,
    private dockingService: DockingBarService,
    private formBuilder: UntypedFormBuilder,
    public spinner: LoadingSpinnerService,
    public roleService: ImpersonateRolesService,
    private analyticsFacade: AnalyticsFacade,
    private win: WindowRef,
    private utilService: UtilService,
    private appService: ApplicationService,
    private feature: FeatureFlagService
  ) {
    // Start the spinner while incoming subscriptions are setup and handled.
    this.spinner.start();

    this.store
      .select(userQuery.selectUserState)
      .pipe(takeUntil(this.stop$))
      .subscribe(currentUser => {
        if (currentUser) {
          this.emailAddress = currentUser.emailAddress;
        }
      });

    this.updatingRegistration = this.store.select(
      BillAccountsSelectors.selectBillAccountsUpdatingRegistration
    );

    this.store
      .select(
        createSelector(
          BillAccountsSelectors.selectBillAccounts,
          BillAccountsSelectors.selectBillAccountsLoading,
          PolicySummarySelectors.selectPolicySummaries,
          PolicySelectors.selectAdvancePolicies,
          BillAccountsSelectors.selectBillAccountsUpdatingRegistration,
          (
            billAccounts,
            billAccountsLoading,
            policySummary,
            advancedPolicies,
            updatingRegistration
          ) =>
            // combine
            ({
              billAccounts: [...billAccounts],
              loading:
                billAccountsLoading ||
                _some(billAccounts, { loading: true }) ||
                updatingRegistration,
              policySummary: [...policySummary],
              advancedPolicies: [...advancedPolicies]
            })
        )
      )
      .pipe(
        takeUntil(this.stop$),
        debounceTime(300),
        filter(state => {
          if (state.loading) {
            this.spinner.start();
          } else {
            this.spinner.stop();
          }
          return !state.loading;
        })
      )
      .subscribe(state => {
        // Set the bill accounts
        this.billAccounts = state.billAccounts;

        // Set the list of nicknames
        state.billAccounts.forEach((billAccount: BillAccount) => {
          if (_get(billAccount, 'billingPreferences.accountNickName')) {
            this.billAccNickNamesList.push(billAccount.billingPreferences.accountNickName);
          }
        });

        // Setup the nickname list validator.
        this.billAccountRegisterForm.setValidators(
          Validators.compose([
            ValidationService.nickNameDuplicationValidator('accountName', this.billAccNickNamesList)
          ])
        );

        // Set the policy data
        this.allPolicies = [];
        /**
         * AS: We are getting empty policy array in the store, so creating a failsafe.
         */

        if (state.policySummary.length) {
          state.policySummary.forEach(policy => {
            if (policy.status === 'Inforce' || policy.status === 'Scheduled') {
              this.allPolicies.push(policy);
            }
          });
        }

        // Initialize the bill account policies
        this.billAccount = this.billAccounts.find(
          billAccount =>
            billAccount.billAccountNumber === this.pendingRegistration.billAccountNumber
        );

        /* To display custom message for advanced policy.
         Get advanced policies from store and look for policy number exists in BA#.
         */
        if (state.advancedPolicies.length) {
          state.advancedPolicies.forEach(aPolicy => {
            if (_has(aPolicy, 'policyNumber') && this.billAccount) {
              if (_some(this.billAccount.policyList, { policyNumber: aPolicy.policyNumber })) {
                this.hasAdvancedPolicy = true;
              }
            }
          });
        }

        // On Change update the listed policy information
        this.setBillAccountPolicies();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Scroll to the top of the page when the bill account changes.
    this.win.nativeWindow.scrollTo(0, 0);

    // This sets up the human-readable "registering 1 of n" when multi-register
    if (_has(changes, 'index.currentValue')) {
      this.index = changes.index.currentValue;

      // Convert index to one based
      this.index++;

      if (this.count > 1) {
        this.optionalCount = '(' + this.index + ' of ' + this.count + ')';
      }
    }

    if (_has(changes, 'pendingRegistration.currentValue')) {
      this.pendingRegistration = changes.pendingRegistration.currentValue;

      this.billAccount = this.billAccounts.find(
        billAccount => billAccount.billAccountNumber === this.pendingRegistration.billAccountNumber
      );

      // On Change update the listed policy information
      this.setBillAccountPolicies();

      this.nickName =
        BillAccountRegistration.NICKNAME_TEXT +
        _get(this.pendingRegistration, 'billAccountNumber', '').slice(-4);

      // Setup the form
      this.setupForm();

      // Setup the heading
      if (this.componentData) {
        this.registerAccountCopy =
          this.componentData['registerAccount'] +
          ' ' +
          new PrettyBillingAcctNum().transform(this.pendingRegistration.billAccountNumber) +
          ' ' +
          this.componentData['registerAccountAfter'];
      }
    }
  }

  ngOnInit() {
    this.saveAndContinueBtnContent = this.copyService.getCopy('shared.saveAndContinueBtn');
    this.saveBtnContent = this.copyService.getCopy('shared.saveBtn');

    // Setup the title
    this.dockingService.registerHeading('Billing & Payments');
    if (this.appService.isApp(Applications.MYACCOUNT_ADMIN)) {
      this.store
        .select(userQuery.selectFirstAndLastName)
        .pipe(
          filter(name => !!name),
          take(1)
        )
        .subscribe(name => {
          this.dockingService.registerSecondaryHeading(`${name}`);
        });
    } else {
      if (this.isContactBillingToggleEnabled) {
        this.dockingService.registerAction(
          new Action('Contact Billing', () => {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [
                  BillingPaymentPaths.BILLING_CONTACT,
                  _get(this, 'pendingRegistration.billAccountNumber')
                ]
              })
            );
          })
        );
      }
    }
    this.componentData = this.copyService.getComponentData('billing');
    this.registerAccountCopy =
      this.componentData['registerAccount'] +
      ' ' +
      new PrettyBillingAcctNum().transform(_get(this, 'pendingRegistration.billAccountNumber')) +
      ' ' +
      this.componentData['registerAccountAfter'];

    // Post Analytics.
    if (this.isMyAccount) {
      this.analyticsFacade.trackPage(BillingAnalytics.registerAccount);
    } else {
      this.adobeAnalyticsLoaded$ = this.store
        .select(AnalyticsSelectors.selectAdobeAnalyticsLoaded)
        .pipe(
          filter(data => data),
          takeUntil(this.stop$)
        );

      this.adobeAnalyticsLoaded$.subscribe(() => {
        this.store.dispatch(
          AnalyticsActions.sendAnalytic({
            options: {
              page: BillingAnalytics.registerAccountAdmin
            }
          })
        );
      });
    }
  }

  ngOnDestroy() {
    // AS: for unassociated bill account, the registration guard anyways resets the bill account store if we
    // navigate away from the registration screen. So checking to make sure we dispatch UpdatePreferenceInitAction only
    // for assocuated billaccount
    if (_get(this.billAccount, 'associated')) {
      this.store.dispatch(
        BillAccountActions.billAccountUpdatePreferenceInit(this.billAccount.billAccountNumber)
      );
    }
    this.stop$.next();
    this.stop$.complete();
    this.spinner.stop();
  }

  registerBillAccount(formData) {
    // generate coorelation id
    const correlationId = this.utilService.generateId();

    // Build the payload
    const payload = this.buildPayload(formData.accountName, formData.reminder, correlationId);

    // add subscription for this bill account with a matching registration correlation id
    this.store.dispatch(BillAccountActions.billAccountUpdateRegistrationSubmit(payload));

    // TODO move this to a localized component-level spinner
    this.spinner.start();

    //filter bill accounts by correlationId
    const selectBillAccountsByCorrelationId = createSelector(
      BillAccountsSelectors.selectBillAccounts,
      (billAccounts: BillAccount[]) => billAccounts.filter(ba => ba.correlationId === correlationId)
    );

    //get a single bill account if exactly one exists
    const selectSingleBillAccount = createSelector(
      selectBillAccountsByCorrelationId,
      (filteredBillAccounts: BillAccount[]) =>
        filteredBillAccounts.length === 1 ? filteredBillAccounts[0] : null
    );

    this.store
      .select(selectSingleBillAccount)
      .pipe(
        filter(billAccount => billAccount !== null),
        first(),
        withLatestFrom(
          this.store.select(
            BillAccountsSelectors.selectMatchedBillAccountNotification,
            this.billAccount.billAccountNumber
          )
        )
      )
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .subscribe(([billingAccount, matchedBillingNotificationState]) => {
        if (matchedBillingNotificationState.updatedRegistration) {
          this.registrationSuccessEvent.emit();
          this.registrationError = false;
          this.errorList = [];
        } else if (matchedBillingNotificationState.updateRegistrationError) {
          this.registrationErrorEvent.emit();
          this.registrationError = true;
          // If the backend throws a sensible string message display it, else display the generic
          // error message.
          if (typeof matchedBillingNotificationState.updateRegistrationError === 'string') {
            // TODO: This needs to be pushed back into the store so we can just render whatever message is there
            const errorMessage =
              matchedBillingNotificationState.updateRegistrationError ===
              INVALID_IMPERSONATION_STATUS_CODE
                ? this.copyService.getCopy('billing.errorMsgsModified[0].202304')
                : matchedBillingNotificationState.updateRegistrationError;

            this.errorList = [
              {
                errorMessage: errorMessage,
                errorLevel: ErrorLevels.HIGH
              }
            ];
          } else {
            this.errorList = [
              {
                errorMessage: this.copyService.getCopy('billing.defaultBillingErrorMessage'),
                errorLevel: ErrorLevels.HIGH
              }
            ];
          }
        }
        this.spinner.stop();
      });
  }

  private setBillAccountPolicies() {
    this.riskDescriptionArr = [];
    if (_has(this.billAccount, 'policyList') && this.billAccount.policyList.length > 0) {
      this.policyDescription = [
        ...this.policyDescription,
        ..._flatmap(this.billAccount.policyList, policy => policy.policyTypeDisplayName)
      ];
      this.riskDescriptionArr = [
        ...this.riskDescriptionArr,
        ..._flatmap(this.billAccount.policyList, policy => policy.riskDescriptionList)
      ];
    }
  }

  private setupForm() {
    let defaultAcceptTerms = false;

    // SK: billingTermsAgree value needs to be selected to continue further while
    // registering multiple bill accounts,
    //  this condition makes billingTermsAgree to True and continue registration process.
    if (!this.acceptTerms) {
      defaultAcceptTerms = true;
    }

    // Setup the form
    this.billAccountRegisterForm = this.formBuilder.group(
      {
        accountName: [
          this.nickName,
          Validators.compose([
            Validators.required,
            Validators.maxLength(25),
            ValidationService.nickNameValidator,
            ValidationService.nickNameNumberLengthValidator
          ])
        ],
        reminder: [this.selectedDate, Validators.compose([Validators.required])]
      },
      {
        validator: Validators.compose([
          ValidationService.nickNameDuplicationValidator('accountName', this.billAccNickNamesList)
        ])
      }
    );

    this.accountName = this.billAccountRegisterForm.controls['accountName'];

    this.billAccountTermsForm = this.formBuilder.group({
      billingTermsAgree: [defaultAcceptTerms]
    });
  }

  private buildPayload(nickName: string, reminder: string, correlationId: string) {
    const formattedReminder = reminder === 'No Reminder' ? '-1' : reminder;

    let policyNumberForRegistration = this.pendingRegistration.policyNumber;
    if (!this.billAccount.associated) {
      const matchingPolicyObj = this.billAccount.policyList.find(policyObj => {
        if (_has(policyObj, 'policyNumber')) {
          return policyObj.policyNumber.includes(policyNumberForRegistration);
        }
      });
      if (matchingPolicyObj) {
        policyNumberForRegistration = matchingPolicyObj.policyNumber;
      }
    }

    const payload = {
      billAccountNumber: this.billAccount.billAccountNumber,
      correlationId: correlationId,
      isUnregister: false,
      registrationData: {
        billAccountNumber: this.billAccount.billAccountNumber,
        policyNumber: policyNumberForRegistration,
        policyDescription: this.policyDescription,
        accountName: nickName,
        reminder: formattedReminder,
        associated: this.billAccount.associated,
        riskDescription: this.riskDescriptionArr,
        emailAddress: this.emailAddress,
        billingSystem: this.billAccount.billingSystem
      }
    };

    return payload;
  }
}
